/**
 * Inventory Bridge Service
 * Bridge between marketplace (sellers) and inventory system
 * Fetches inventory data for products to display in My Products page
 *
 * Uses the existing /api/v1/mfg-production-batches/ready-for-sale endpoint
 * which contains all inventory quantity data (available, reserved, total)
 */

import { getEnvironmentConfig } from '@/config/environment';

/**
 * Inventory data structure
 */
export interface ProductInventoryData {
  productId: string;
  totalQuantity: number;
  availableQuantity: number;
  reservedQuantity: number;
  unit: string;
  lastUpdated: string;
}

// Cache for batch data to avoid repeated API calls
let batchDataCache: Map<string, ProductInventoryData> | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 30000; // 30 seconds cache

/**
 * Fetch all batch data and cache it
 * Uses the existing ready-for-sale endpoint which has all inventory data
 */
async function fetchAndCacheBatchData(): Promise<Map<string, ProductInventoryData>> {
  const config = getEnvironmentConfig();
  const API_BASE_URL = config.microservices.commerce || config.apiBaseUrl;

  try {
    console.log('[InventoryBridge] Fetching batch data from ready-for-sale endpoint');

    const response = await fetch(
      `${API_BASE_URL}/api/v1/mfg-production-batches/ready-for-sale`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch batch data: ${response.statusText}`);
    }

    const result = await response.json();
    const batches = result.data || [];

    // Group batches by productId and aggregate quantities
    const productQuantities = new Map<string, ProductInventoryData>();

    batches.forEach((batch: any) => {
      const productId = String(batch.dungProductId);
      const existing = productQuantities.get(productId);

      if (existing) {
        // Aggregate quantities for same product from multiple batches
        existing.totalQuantity += Number(batch.outputQuantityKg) || 0;
        existing.availableQuantity += Number(batch.availableQuantityKg) || 0;
        existing.reservedQuantity += Number(batch.reservedQuantityKg) || 0;
        existing.lastUpdated = batch.updatedAt || existing.lastUpdated;
      } else {
        productQuantities.set(productId, {
          productId,
          totalQuantity: Number(batch.outputQuantityKg) || 0,
          availableQuantity: Number(batch.availableQuantityKg) || 0,
          reservedQuantity: Number(batch.reservedQuantityKg) || 0,
          unit: 'kg',
          lastUpdated: batch.updatedAt || new Date().toISOString(),
        });
      }
    });

    console.log(`[InventoryBridge] ✅ Cached inventory data for ${productQuantities.size} products`);

    // Update cache
    batchDataCache = productQuantities;
    cacheTimestamp = Date.now();

    return productQuantities;
  } catch (error) {
    console.warn('[InventoryBridge] ⚠️ Failed to fetch batch data:', error);
    return new Map();
  }
}

/**
 * Get cached batch data or fetch if cache is stale
 */
async function getBatchData(): Promise<Map<string, ProductInventoryData>> {
  const now = Date.now();

  if (batchDataCache && (now - cacheTimestamp) < CACHE_TTL) {
    console.log('[InventoryBridge] Using cached batch data');
    return batchDataCache;
  }

  return fetchAndCacheBatchData();
}

/**
 * Fetch inventory data for a single product
 * @param productId - Product ID
 * @returns Inventory data for the product
 */
export async function getProductQuantity(
  productId: string
): Promise<ProductInventoryData> {
  try {
    console.log(`[InventoryBridge] Fetching quantity for product: ${productId}`);

    const batchData = await getBatchData();
    const inventory = batchData.get(productId);

    if (inventory) {
      console.log(`[InventoryBridge] ✅ Found quantity for product ${productId}:`, inventory);
      return inventory;
    }

    console.log(`[InventoryBridge] ⚠️ No inventory found for product ${productId}, returning defaults`);
    return {
      productId,
      totalQuantity: 0,
      availableQuantity: 0,
      reservedQuantity: 0,
      unit: 'kg',
      lastUpdated: new Date().toISOString(),
    };
  } catch (error) {
    console.warn(
      `[InventoryBridge] ⚠️ Failed to fetch inventory for ${productId}:`,
      error
    );
    return {
      productId,
      totalQuantity: 0,
      availableQuantity: 0,
      reservedQuantity: 0,
      unit: 'kg',
      lastUpdated: new Date().toISOString(),
    };
  }
}

/**
 * Batch fetch inventory data for multiple products
 * More efficient than calling getProductQuantity multiple times
 * @param productIds - Array of product IDs
 * @returns Map of productId → inventory data
 */
export async function getProductsQuantities(
  productIds: string[]
): Promise<Map<string, ProductInventoryData>> {
  try {
    console.log(
      `[InventoryBridge] Batch fetching quantities for ${productIds.length} products`
    );

    const batchData = await getBatchData();
    const inventoryMap = new Map<string, ProductInventoryData>();

    for (const productId of productIds) {
      const inventory = batchData.get(productId);
      if (inventory) {
        inventoryMap.set(productId, inventory);
      } else {
        // Return default values for products not in batch data
        inventoryMap.set(productId, {
          productId,
          totalQuantity: 0,
          availableQuantity: 0,
          reservedQuantity: 0,
          unit: 'kg',
          lastUpdated: new Date().toISOString(),
        });
      }
    }

    console.log(
      `[InventoryBridge] ✅ Batch fetched quantities for ${inventoryMap.size} products`
    );

    return inventoryMap;
  } catch (error) {
    console.warn('[InventoryBridge] ⚠️ Batch inventory fetch failed:', error);

    // Return default values for all requested products
    const fallbackMap = new Map<string, ProductInventoryData>();
    for (const productId of productIds) {
      fallbackMap.set(productId, {
        productId,
        totalQuantity: 0,
        availableQuantity: 0,
        reservedQuantity: 0,
        unit: 'kg',
        lastUpdated: new Date().toISOString(),
      });
    }

    return fallbackMap;
  }
}

/**
 * Deduct quantity from a product when an order is created
 * Note: This operation is handled by the order creation flow in commerce-service
 * This function is kept for API compatibility but logs a warning
 * @param productId - Product ID
 * @param quantityToDeduct - Quantity to deduct from available
 * @param orderId - Associated order ID
 * @returns Success status and remaining quantity
 */
export async function deductProductQuantity(
  productId: string,
  quantityToDeduct: number,
  orderId: string
): Promise<{ success: boolean; remainingQuantity: number; error?: string }> {
  console.warn(
    `[InventoryBridge] deductProductQuantity called - This should be handled by order creation flow`
  );
  console.log(
    `[InventoryBridge] Product: ${productId}, Qty: ${quantityToDeduct}, Order: ${orderId}`
  );

  // Invalidate cache so next fetch gets updated quantities
  batchDataCache = null;
  cacheTimestamp = 0;

  // Return success - actual deduction is handled by backend during order creation
  return {
    success: true,
    remainingQuantity: 0,
  };
}

/**
 * Get current quantity status for a product
 * Useful for checking if product is out of stock
 * @param productId - Product ID
 * @returns Current quantity status
 */
export async function getProductQuantityStatus(productId: string): Promise<{
  isOutOfStock: boolean;
  isLowStock: boolean;
  availableQuantity: number;
  unit: string;
}> {
  const inventory = await getProductQuantity(productId);

  return {
    isOutOfStock: inventory.availableQuantity === 0,
    isLowStock: inventory.availableQuantity < 20 && inventory.availableQuantity > 0,
    availableQuantity: inventory.availableQuantity,
    unit: inventory.unit,
  };
}

/**
 * Force refresh the inventory cache
 * Call this after operations that change inventory
 */
export async function refreshInventoryCache(): Promise<void> {
  console.log('[InventoryBridge] Force refreshing inventory cache');
  batchDataCache = null;
  cacheTimestamp = 0;
  await fetchAndCacheBatchData();
}
